import { Fragment, ReactElement, useEffect, useMemo, useState } from 'react';
import Breadcrumbs, { BreadcrumbItem } from 'shell/components/shared/breadcrumb';
import { useNavigate, useParams } from 'react-router-dom';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponse } from 'shell/models/http-response';
import { ActCategory } from 'shell/models/act-category-list';
import { __ACT_API_URL__ } from 'shell/constants/environment';
import { useEventCallback } from '@mui/material';
import { IdStateNotification } from 'notifications-from-civil-status/constants/id-state-notification';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { toast } from 'react-toastify';
import RegistrationMarriagePage from 'mutation/pages/registration-marriage/resident';
import { RegistrationMarriageFormVM } from 'mutation/view-models/registration-marriage';
import { transformMarriage } from 'notifications-from-civil-status/utils/transform-marriage';
import { useFilterTable } from 'shell/hooks/use-filter-table';
import { ResearchNotifications } from 'notifications-from-civil-status/view-models/notifications-list';
import ErrorModal from 'shell/components/shared/modal-error';

interface ResearchMarriageDetailNotificationsSharedProps {
  goBackUrl?: string;
  goBackUrlDetail?: string;
  items?: BreadcrumbItem[];
  isOther?: boolean;
  isFromCivilStatus?: boolean;
}

const ResearchMarriageDetailNotificationsShared = ({
  goBackUrl = '',
  goBackUrlDetail = '',
  items = [],
  isOther,
  isFromCivilStatus,
}: ResearchMarriageDetailNotificationsSharedProps): ReactElement => {
  const [modalError, setModalError] = useState<{ message?: string; isError?: boolean }>({
    message: '',
    isError: false,
  });

  const filterTable = useFilterTable<ResearchNotifications>();

  const params = useParams();

  const idNotifica = params.idNotifica || '';

  const idAnsc = useMemo(() => {
    return params.id || undefined;
  }, [params.id]);

  const [dataEvent, getDataEvent] = useHttpFetch<
    HttpResponse<ActCategory>,
    RegistrationMarriageFormVM
  >(`${__ACT_API_URL__}/consultazione/evento`, {
    onBootstrap: false,
    options: {
      errorCatching: true,
      method: 'POST',
      loading: true,
      data: {},
    },
    transformer: (res) => {
      const castedRes = transformMarriage(res, filterTable.values?.isMaritoResidenceMilano);
      if (castedRes.error) {
        // errori dal servizio
        setModalError({ message: castedRes.error, isError: true });
      }

      if (!castedRes.data.marriage?.idAnprSoggetto) {
        // mancanza dell'id anpr per il coniuge
        setModalError({
          message: 'Attenzione - manca idANPR del coniuge, effettuare la ricerca',
          isError: false,
        });
      }

      return castedRes.data;
    },
  });

  useEffect(() => {
    if (idAnsc && !isOther) {
      getDataEvent({
        data: {
          criteriRicercaEvento: {
            idAnsc,
          },
        },
      });
    }
  }, [idAnsc]);

  const saveData = useHttpClient(`${__ACT_API_URL__}/aggiornamento-anagrafe/save`, {
    options: {
      method: 'POST',
      loading: true,
      errorCatching: true,
    },
  });

  const navigate = useNavigate();

  const onConfirm = useEventCallback(() => {
    if (!idNotifica) {
      return;
    }

    (
      saveData({
        data: {
          key: {
            idAggiornamentoAnagrafe: idNotifica,
          },
          cdStatoAggiornamento: IdStateNotification.RegistrazioneEffettuata,
        },
      }) as ResponsePromise<void>
    ).then(() => {
      toast.success('Operazione eseguita con successo');
      navigate(goBackUrlDetail);
    });
  });

  const onHandleExit = useEventCallback(() => {
    if (!idNotifica) {
      return;
    }

    (
      saveData({
        data: {
          key: {
            idAggiornamentoAnagrafe: idNotifica,
          },
          cdStatoAggiornamento: IdStateNotification.DaRegistrare,
        },
      }) as ResponsePromise<void>
    ).then(() => {
      toast.success('Operazione eseguita con successo');
      navigate(goBackUrl);
    });
  });

  return (
    <Fragment>
      <Breadcrumbs items={items} />
      {dataEvent.data && !dataEvent.pristine && !dataEvent.isLoading && !dataEvent.hasErrors ? (
        <RegistrationMarriagePage
          isWithoutBreadcrumbs
          initialValues={(dataEvent.data || {}) as RegistrationMarriageFormVM}
          onConfirm={onConfirm}
          onHandleExit={onHandleExit}
          disabledFromAct
          goBackUrl={goBackUrl}
          isFromCivilStatus={isFromCivilStatus}
        />
      ) : undefined}
      {Boolean(modalError.message) ? (
        <ErrorModal
          message={modalError.message || ''}
          onClose={() => {
            if (modalError.isError) {
              navigate(goBackUrl || '');
              return;
            }
            setModalError({ message: '', isError: false });
          }}
          open={Boolean(modalError)}
        />
      ) : undefined}
    </Fragment>
  );
};

ResearchMarriageDetailNotificationsShared.displayName =
  'Research Marriage Detail Notifications Shared component';

export default ResearchMarriageDetailNotificationsShared;
